import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import NetskopeConnectForm from "./NetskopeConnectForm";
import netskope_step_1 from "./images/netskope_step_1.png";
import netskope_step_2 from "./images/netskope_step_2.png";
import netskope_step_3 from "./images/netskope_step_3.png";

const Netskope = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("netskope");
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const { registerNetskopeIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    let tenant_url = values.tenant_url;
    if (values.tenant_url.endsWith("/")) {
      tenant_url = values.tenant_url.slice(0, -1);
    }
    if (!tenant_url.startsWith("https://")) {
      tenant_url = "https://" + tenant_url;
    }

    registerNetskopeIntegration(values.api_token, tenant_url)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Netskope integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Netskope and prioritize the suggested remediations."
            : ""
        }
        image={logos.NetskopeLogo}
        name={"Netskope"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Netskope is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Netskope">
                  <Typography variant={"body2"}>
                    Login to your Netskope account
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Copy the Tenant URL">
                  <Typography variant={"body2"}>
                    Copy the Tenant URL from the domain name in the URL.
                  </Typography>
                  <Typography variant={"body2"}>
                    {" "}
                    {
                      "It should look like this: https://<tenant-name>.goskope.com"
                    }
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Create a new API token">
                  <Typography variant={"body2"}>
                    From the main menu go to the settings page
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: netskope_step_1,
                        altext: "Netskope API Tokens Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>Choose Tools</Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: netskope_step_2,
                        altext: "Netskope API Tokens Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Copy the V1 API token
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: netskope_step_3,
                        altext: "Netskope API Tokens Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Paste the API Token and the Tenant URL here">
                  <Box width={700}>
                    <NetskopeConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default Netskope;
